import BPCConfigService from '@core/services/bpc-config.service';
/* eslint-disable @typescript-eslint/no-explicit-any */
import { FormFlags, FormMutations } from './../interfaces/form-state';
// profile/index.ts
// import BPCMessageService from '@core/services/bpc-messages.service';
import BPCModelFactoryService from '@core/services/bpc-model-factory.service';
import BPCGenericApiResponse from '@core/interfaces/bpc-generic-api-response';
import { FormFieldConfig } from '@shared/interfaces/config';
import { Observable, forkJoin } from 'rxjs';
import {
  FormState,
  FormConfig,
  StateOfField,
  FormGetters,
  FormActions,
} from '@shared/interfaces/form-state';
import { setProp, getProp, deleteProp } from '@shared/helpers/operators';
import { convertModelToFormData, prepareToSave } from '@shared/helpers/process-entity';
import BPCAbstractRequestService from '@core/services/bpc-abstract-request.service';
import BPCApiRouteHelper from '@core/helpers/bpc-api-route.helper';
import { bpcApi } from '@core/services/bpc-api.service';
import { ApiRouteType } from '@core/interfaces/core-services';

const bpcRouterHelper = new BPCApiRouteHelper();

// const bpcMessage = new BPCMessageService();
const bpcConfig = new BPCConfigService();
const bpcModelFactory = new BPCModelFactoryService();

export function stateFactory<M>(model: M): FormState<M> {
  return {
    model: model,
    rawModel: model,
    configuration: {
      fields: {},
      name: null,
      base: null,
      hasMedia: null,
    },

    stateOfFields: {},
    currentAction: null,
    saveEntitySubscription: null,
    saveEntityObservable: null,
    mode: 'api',
    extraData: null,
    searchEntitySubscription: null,
    searchEntityObservable: null,
    withDraft: false,
    flags: {
      loadingConfig: false,
      loadingModel: false,
      saving: false,
      searching: false,
      initialized: false,
      isDirty: false,
      isTouched: false,
    },
  };
}

// Synchronous functions to get data from state (can be composed properties)
export function gettersFactory<M>(): FormGetters<FormState<M>, M> {
  return {
    model: (state: FormState<M>): M => {
      return state.model;
    },

    rawModel: (state: FormState<M>): M => {
      return state.rawModel;
    },

    mode: (state): 'api' | 'storage' => {
      return state.mode;
    },

    configuration: (state: FormState<M>): FormConfig => {
      return state.configuration;
    },

    stateOfFields: (state: FormState<M>): Record<string, StateOfField> => {
      return state.stateOfFields;
    },

    flags: (state: FormState<M>): FormFlags => {
      return state.flags;
    },

    initialized: (state: FormState<M>): boolean => {
      return state.flags.initialized;
    },

    isLoading: (state: FormState<M>): boolean => {
      return state.flags.loadingConfig || state.flags.loadingModel;
    },

    isSearching: (state: FormState<M>): boolean => {
      return state.flags.searching;
    },

    withDraft: (state: FormState<M>): boolean => {
      return state.withDraft || false;
    },

    isSaving: (state: FormState<M>): boolean => {
      return state.flags.saving;
    },

    fieldsAreSet: (state: FormState<M>): boolean => {
      return Object.keys(state.configuration.fields).length > 0;
    },

    fieldsObject: (state: FormState<M>): Record<string, FormFieldConfig> => {
      return state.configuration.fields;
    },
    isDirty: (state: FormState<M>): boolean => {
      return state.flags.isDirty;
    },
    isTouched: (state: FormState<M>): boolean => {
      return state.flags.isTouched;
    },
    currentAction: (state: FormState<M>): string => {
      return state.currentAction;
    },
    extraData: (state: FormState<M>): any => {
      return state.extraData;
    },
  };
}

// Asynchornous functions to get data usually from an api call
export function actionsFactory<M>(): FormActions<FormState<M>> {
  return {
    // Get the form config
    fetchFormConfig: ({ commit }, data: { entity?: string; formName?: string; path?: string }) => {
      commit('setLoadingConfig', true);
      const entityFormConfigSlug = data.path || `${data.entity}.forms.${data.formName}`;
      return bpcConfig
        .getEntityConfig(entityFormConfigSlug)
        .subscribe((res: BPCGenericApiResponse) => {
          const disabled =
            res.data.disabled === 'false' ||
            res.data.disabled === false ||
            res.data.disabled === '0' ||
            res.data.disabled === 0
              ? false
              : true;
          const config = {
            ...res.data,
            disabled: disabled,
          };
          commit('setFormConfig', config);
          commit('setLoadingConfig', false);
        });
    },

    fetchEntity: ({ commit }, data: { entity?: string; id?: number; path?: string }) => {
      commit('setLoadingModel', true);

      const route = data.path || `${bpcRouterHelper.getEntityPath(data.entity)}/${data.id}`;

      return bpcApi.get(route).subscribe((res: BPCGenericApiResponse) => {
        commit('setRawModel', res.data);
        commit('setLoadingModel', false);
      });
    },

    saveEntity: async (
      { commit, state, getters },
      data: {
        entity: M;
        entityId: number;
        successText: string;
        hasMedia: boolean;
        requestService: BPCAbstractRequestService;
        apiRouteType: ApiRouteType;
        withDraft: boolean;
        saveEntityPath: string;
      },
    ) => {
      commit('setSaving', true);

      const result = await new Promise((resolve, reject) => {
        if (state.saveEntitySubscription) {
          state.saveEntitySubscription.unsubscribe();
        }
        let entityCopy = { ...data.entity };
        entityCopy = prepareToSave(entityCopy, getters.fieldsObject);

        const method = data.entityId ? 'put' : null;
        if (data.hasMedia && data.entityId) {
          const formData = convertModelToFormData(entityCopy, method);
          state.saveEntityObservable = bpcApi.putWithMedia(data.saveEntityPath, formData);
        }

        if (data.hasMedia && !data.entityId) {
          const formData = convertModelToFormData(entityCopy, method);
          state.saveEntityObservable = bpcApi.postWithMedia(data.saveEntityPath, formData);
        }

        if (!data.hasMedia && data.entityId) {
          state.saveEntityObservable = data.requestService.put(
            data.saveEntityPath,
            entityCopy,
            null,
            data.apiRouteType,
            data.withDraft,
          );
        }

        if (!data.hasMedia && !data.entityId) {
          state.saveEntityObservable = data.requestService.post(
            data.saveEntityPath,
            entityCopy,
            null,
            data.apiRouteType,
            data.withDraft,
          );
        }
        state.saveEntitySubscription = state.saveEntityObservable.subscribe(
          (res: BPCGenericApiResponse) => {
            commit('setSaving', false);
            resolve(res);
          },
          error => {
            commit('setSaving', false);
            reject(error);
          },
        );
      });

      return result;
    },

    searchEntity: async (
      { commit, state, getters },
      data: { entity: M; requestService: BPCAbstractRequestService; searchEntityPath: string },
    ) => {
      commit('setSearching', true);
      commit('setCurrentAction', 'search');

      const result = await new Promise((resolve, reject) => {
        if (state.searchEntitySubscription) {
          state.searchEntitySubscription.unsubscribe();
        }

        let entityCopy = { ...data.entity };
        entityCopy = prepareToSave(entityCopy, state.configuration.fields);

        state.searchEntityObservable = data.requestService.post(data.searchEntityPath, entityCopy);
        state.searchEntitySubscription = state.searchEntityObservable.subscribe(
          (res: BPCGenericApiResponse) => {
            commit('setInitialModel', {
              fields: getters.fieldsObject,
              model: res.data,
            });
            commit('setRawModel', {
              fields: getters.fieldsObject,
              model: res.data,
            });
            commit('setIsSearched', true);
            commit('setSearching', false);

            resolve(res);
          },
          error => {
            commit('setSearching', false);
            reject(error);
          },
        );
      });
      return result;
    },

    initializeFormModel: (
      { commit, getters },
      data: {
        entity: string;
        fetchEntity: boolean;
        id?: number;
        formName?: string;
        configPath?: string;
        fetchPath?: string;
        forceUpdateConfig?: boolean;
        requestService: BPCAbstractRequestService;
      },
    ) => {
      commit('setInitialized', false);

      commit(`cleanModel`);
      commit(`cleanRawModel`);

      // const fetchEntity = dispatch('fetchEntity', {entity: entity, ud: identity, path: fetchPath});

      commit('setLoadingConfig', true);
      commit('setLoadingModel', true);

      const entityFormConfigSlug = data.configPath || `${data.entity}.forms.${data.formName}`;

      const initialization: {
        config?: Observable<BPCGenericApiResponse>;
        entity?: Observable<BPCGenericApiResponse>;
      } = {};

      if (!getters.fieldsAreSet || data.forceUpdateConfig) {
        initialization.config = bpcConfig.getEntityConfig(entityFormConfigSlug);
      }

      if (data.fetchEntity) {
        const fetchRoute =
          data.fetchPath || `${bpcRouterHelper.getEntityPath(data.entity)}/${data.id}`;
        initialization.entity = data.requestService.get(fetchRoute);
      }

      if (Object.keys(initialization).length === 0) {
        commit('setInitialModel', {
          fields: getters.configuration.fields,
          model: {},
          extraData: getters.extraData,
        });
      }

      return forkJoin(initialization).subscribe(
        (res: { config?: BPCGenericApiResponse; entity?: BPCGenericApiResponse }) => {
          console.log('forkjoinRes', res);
          if (res.config) {
            commit('setFormConfig', res.config.data);
          }

          if (res.entity) {
            commit('setRawModel', res.entity.data);
          }

          commit('setInitialModel', {
            fields: getters.configuration.fields,
            model: getters.rawModel,
            extraData: getters.extraData,
          });
          commit('setInitialized', true);
          console.log('initialized');
        },
        error => {
          console.log(error);
        },
        () => {
          commit('setLoadingConfig', false);
          commit('setLoadingModel', false);
        },
      );
    },
  };
}

// The vuex way of changing a state property
export function mutationsFactory<M>(model: M): FormMutations<FormState<M>, M> {
  return {
    setFormConfig: (state, config: FormConfig) => {
      setProp(state, 'configuration', config);
    },
    setLoadingConfig: (state, value: boolean) => {
      state.flags.loadingConfig = value;
    },
    setLoadingModel: (state, value: boolean) => {
      state.flags.loadingModel = value;
    },
    setSearching: (state, value: boolean) => {
      state.flags.searching = value;
    },
    setSaving: (state, value: boolean) => {
      state.flags.saving = value;
    },
    setIsDirty: (state, value: boolean) => {
      state.flags.isDirty = value;
    },
    setIsTouched: (state, value: boolean) => {
      state.flags.isTouched = value;
    },
    setInitialized: (state: FormState<M>, value: boolean) => {
      state.flags.initialized = value;
    },
    setRawModel: (state, model: M) => {
      setProp(state, 'rawModel', model);
    },
    setModel: (state, model: M) => {
      setProp(state, 'model', model);
    },
    setModelProp: (state, data: { prop: string; value: any }): void => {
      setProp(state.model, data.prop, data.value);
    },
    deleteModelProp: (state, prop: string): void => {
      deleteProp(state.model, prop);
    },
    getModelProp: (state, prop: string): void => {
      getProp(state.model, prop);
    },
    cleanModel: (state: FormState<M>) => {
      // state.flags.initialized = false;
      state.model = null;
    },
    cleanRawModel: (state: FormState<M>) => {
      // state.flags.initialized = false;
      state.rawModel = null;
    },
    setInitialState: (state: FormState<M>) => {
      // From abstract state
      state.model = null;
      state.rawModel = null;
      state.currentAction = null;
      state.saveEntitySubscription = null;
      state.saveEntityObservable = null;

      state.searchEntitySubscription = null;
      state.searchEntityObservable = null;
      state.mode = 'api';
      state.flags = {
        loadingConfig: false,
        loadingModel: false,
        saving: false,
        searching: false,
        initialized: false,
        isDirty: false,
        isTouched: false,
      };
    },

    setInitialModel: (
      state,
      data: { fields: Record<string, FormFieldConfig>; model: M; extraData: any },
    ) => {
      setProp(
        state,
        'model',
        bpcModelFactory.getInitialFormModel(
          Object.values(data.fields),
          null,
          data.model,
          data.extraData,
        ),
      );
      state.flags.initialized = true;
      console.log('STATE', state);
    },
    setCurrentAction: (state, action: string) => {
      state.currentAction = action;
    },
    setMode: (state, value: 'api' | 'storage'): void => {
      state.mode = value;
    },

    setWithDraft: (state, value: boolean): void => {
      state.withDraft = value;
    },

    setExtraData: (state, value: any): void => {
      state.extraData = value;
    },
  };
}
